import React, { useCallback, useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import API_BASE_URL from "../config";
import { publicUrl } from "../publicOrigin";
import "./ShareUploadLinkModal.css";

/**
 * "Share with co-worker" for one event: the studio sets a PIN and an expiry,
 * gets a link (and QR) that opens UploadLinkPage. Whoever has the link and the
 * PIN can upload photos into this event — nothing else.
 *
 * The PIN is only ever shown here, right after creation. The API stores a hash
 * and never returns it, so a studio that forgets the PIN regenerates the link.
 */

const EXPIRY_OPTIONS = [
  { value: "24", label: "24 hours" },
  { value: "72", label: "3 days" },
  { value: "168", label: "7 days" },
  { value: "", label: "Until I revoke it" },
];

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function randomPin() {
  const n = Math.floor(Math.random() * 1000000);
  return String(n).padStart(6, "0");
}

function formatExpiry(iso) {
  if (!iso) return "Active until revoked";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `Expires ${d.toLocaleString()}`;
}

function ShareUploadLinkModal({ event, onClose }) {
  const [loading, setLoading] = useState(true);
  const [link, setLink] = useState(null);
  const [pin, setPin] = useState("");
  const [expiry, setExpiry] = useState("168");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  // The PIN just used to create the current link — shown once, never re-fetched.
  const [createdPin, setCreatedPin] = useState("");
  const [showForm, setShowForm] = useState(false);

  const eventId = event?.id;

  const load = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/upload-link`, { headers: authHeaders() });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not load the share link");
      setLink(data.link || null);
      setShowForm(!data.link);
    } catch (err) {
      setError(err.message);
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    setLink(null);
    setPin("");
    setCreatedPin("");
    setNotice("");
    load();
  }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const shareUrl = link ? publicUrl(link.url_path) : "";

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!/^\d{4,8}$/.test(pin)) {
      setError("PIN must be 4 to 8 digits");
      return;
    }
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/upload-link`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ pin, expiresInHours: expiry === "" ? null : Number(expiry) }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || "Could not create the share link");
      setLink(data.link);
      setCreatedPin(pin);
      setPin("");
      setShowForm(false);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!window.confirm("Revoke this link? Anyone using it will lose upload access immediately.")) return;
    setSaving(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE_URL}/api/events/${eventId}/upload-link`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!res.ok && res.status !== 204) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.message || "Could not revoke the link");
      }
      setLink(null);
      setCreatedPin("");
      setShowForm(true);
      setNotice("Link revoked.");
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const copy = async (text, what) => {
    try {
      await navigator.clipboard.writeText(text);
      setNotice(`${what} copied to clipboard`);
      setTimeout(() => setNotice(""), 2000);
    } catch {
      setNotice("Copy failed — select the text and copy it manually");
    }
  };

  const downloadQr = () => {
    const svgEl = document.getElementById("sul-qr-svg");
    if (!svgEl) return;
    const size = 1024;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    const svgData = new XMLSerializer().serializeToString(svgEl);
    const img = new Image();
    img.onload = () => {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, size, size);
      ctx.drawImage(img, 0, 0, size, size);
      const a = document.createElement("a");
      a.download = `${event?.event_name || "event"}-upload-link-qr.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
    };
    img.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgData);
  };

  if (!event) return null;

  return (
    <div className="sul-overlay" onClick={onClose} role="presentation">
      <div className="sul-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="sul-title">
        <h3 id="sul-title">Share with co-worker</h3>
        <p className="sul-sub">
          A second shooter or assistant can upload photos into <strong>{event.event_name}</strong> with this link and PIN.
          They can’t see other events, edit anything, or delete photos.
        </p>

        {loading ? (
          <p className="sul-loading">Loading…</p>
        ) : (
          <>
            {link && !showForm && (
              <div className="sul-active">
                {createdPin && (
                  <div className="sul-pin-reveal">
                    <span className="sul-pin-label">PIN</span>
                    <code className="sul-pin-value">{createdPin}</code>
                    <button type="button" className="sul-btn-ghost" onClick={() => copy(createdPin, "PIN")}>Copy PIN</button>
                    <span className="sul-pin-hint">Shown once — share it separately from the link.</span>
                  </div>
                )}
                <div className="sul-qr">
                  <QRCodeSVG id="sul-qr-svg" value={shareUrl} size={180} level="H" includeMargin bgColor="#ffffff" fgColor="#14142b" />
                </div>
                <div className="sul-link-row">
                  <input type="text" readOnly value={shareUrl} className="sul-link-input" onFocus={(e) => e.target.select()} aria-label="Upload link" />
                  <button type="button" className="sul-btn-primary" onClick={() => copy(shareUrl, "Link")}>Copy</button>
                </div>
                <div className="sul-meta">
                  <span>{formatExpiry(link.expires_at)}</span>
                  <span>Used {link.use_count || 0} time{link.use_count === 1 ? "" : "s"}</span>
                </div>
                <div className="sul-actions">
                  <button type="button" className="sul-btn-secondary" onClick={downloadQr}>Download QR</button>
                  <button type="button" className="sul-btn-secondary" onClick={() => { setShowForm(true); setCreatedPin(""); }}>New PIN / link</button>
                  <button type="button" className="sul-btn-danger" onClick={handleRevoke} disabled={saving}>Revoke</button>
                </div>
              </div>
            )}

            {showForm && (
              <form className="sul-form" onSubmit={handleCreate}>
                {link && (
                  <p className="sul-warn">Creating a new link replaces the current one. Anyone still using the old link will be signed out.</p>
                )}
                <label className="sul-field">
                  <span>PIN (4–8 digits)</span>
                  <div className="sul-pin-row">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={8}
                      value={pin}
                      onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
                      placeholder="e.g. 4821"
                      className="sul-input"
                      autoFocus
                    />
                    <button type="button" className="sul-btn-ghost" onClick={() => setPin(randomPin())}>Generate</button>
                  </div>
                </label>
                <label className="sul-field">
                  <span>Link expires in</span>
                  <select value={expiry} onChange={(e) => setExpiry(e.target.value)} className="sul-input">
                    {EXPIRY_OPTIONS.map((o) => <option key={o.label} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                <div className="sul-actions">
                  <button type="submit" className="sul-btn-primary" disabled={saving || pin.length < 4}>
                    {saving ? "Creating…" : link ? "Replace link" : "Create link"}
                  </button>
                  {link && (
                    <button type="button" className="sul-btn-secondary" onClick={() => setShowForm(false)} disabled={saving}>Cancel</button>
                  )}
                </div>
              </form>
            )}
          </>
        )}

        {error && <p className="sul-error" role="alert">{error}</p>}
        {notice && <p className="sul-notice" role="status">{notice}</p>}

        <div className="sul-footer">
          <button type="button" className="sul-btn-close" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

export default ShareUploadLinkModal;
